import { ThemeEvents } from '@theme/events';

/* global Theme */

/** @type {Promise<Set<string>> | null} */
let inFlight = null;

/**
 * @returns {Promise<Set<string>>}
 */
async function fetchCartProductIds() {
  if (inFlight) return inFlight;
  inFlight = fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
    .then((r) => r.json())
    .then((cart) => new Set((cart.items || []).map((/** @type {any} */ i) => String(i.product_id))))
    .catch(() => new Set())
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

/**
 * @param {Element} el
 * @param {boolean} inCart
 */
function applyAddToCartState(el, inCart) {
  const button = el.querySelector('button[ref="addToCartButton"], button[name="add"]');
  if (!button) return;
  if (inCart) {
    button.setAttribute('disabled', '');
  } else {
    if (/** @type {HTMLElement} */ (el).dataset.cameSoon === 'true') return;
    if (button.getAttribute('data-force-disabled') === 'true') return;
    button.removeAttribute('disabled');
  }
}

/**
 * @param {Element} el
 * @param {boolean} inCart
 */
function applyQuantityState(el, inCart) {
  const isCartLine = /** @type {HTMLElement} */ (el).dataset.heritageCartLine === 'true';
  const input = el.querySelector('input[type="number"]');
  const buttons = el.querySelectorAll('button.quantity-minus, button.quantity-plus');

  if (isCartLine) {
    const lock = inCart;
    buttons.forEach((b) => (lock ? b.setAttribute('disabled', '') : b.removeAttribute('disabled')));
    if (input) {
      if (lock) input.setAttribute('disabled', '');
      else input.removeAttribute('disabled');
    }
    return;
  }

  buttons.forEach((b) => (inCart ? b.setAttribute('disabled', '') : b.removeAttribute('disabled')));
  if (input) {
    const htmlInput = /** @type {HTMLInputElement} */ (input);
    if (inCart) htmlInput.setAttribute('disabled', '');
    else htmlInput.removeAttribute('disabled');
    htmlInput.setAttribute('max', '1');
    if (Number(htmlInput.value) > 1) htmlInput.value = '1';
  }
}

const optimisticallyLocked = new Set();

/**
 * @param {Element} button
 * @param {boolean} inCart
 */
function applyUsfButtonState(button, inCart) {
  if (inCart) {
    button.setAttribute('disabled', '');
  } else {
    button.removeAttribute('disabled');
  }
}

/**
 * @param {string | number | null | undefined} productId
 */
function lockForProduct(productId) {
  if (!productId) return;
  const id = String(productId);
  optimisticallyLocked.add(id);
  document
    .querySelectorAll(`add-to-cart-component[data-heritage-source="true"][data-product-id="${id}"]`)
    .forEach((el) => applyAddToCartState(el, true));
  document
    .querySelectorAll(`quantity-selector-component[data-heritage-source="true"][data-product-id="${id}"]`)
    .forEach((el) => applyQuantityState(el, true));
  document
    .querySelectorAll(`button.product-card__add-to-cart[data-heritage-source="true"][data-product-id="${id}"]`)
    .forEach((btn) => applyUsfButtonState(btn, true));
}

const HERITAGE_LIMIT_MESSAGE = 'Only 1 quantity allowed per Heritage Coins product.';

const correctingLines = new Set();

/**
 * @param {Element} qtyEl
 * @param {string | number} line
 */
function showHeritageLineError(qtyEl, line) {
  const row =
    qtyEl.closest('[ref="cartItemRows[]"]') ||
    qtyEl.closest('tr') ||
    qtyEl.parentElement;
  if (!row) return;
  const container = row.querySelector(`[ref="cartItemErrorContainer-${line}"]`);
  const textEl = row.querySelector(`[ref="cartItemError-${line}"]`);
  if (container) container.classList.remove('hidden');
  if (textEl) textEl.textContent = HERITAGE_LIMIT_MESSAGE;
}

/**
 * @param {Element} qtyEl
 * @param {string | number} line
 */
async function enforceHeritageLineLimit(qtyEl, line) {
  if (correctingLines.has(line)) return;
  correctingLines.add(line);
  try {
    const res = await fetch(`${Theme.routes.cart_change_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: Number(line), quantity: 1 }),
    });
    if (!res.ok) return;
    showHeritageLineError(qtyEl, line);
    const input = qtyEl.querySelector('input[type="number"]');
    if (input) /** @type {HTMLInputElement} */ (input).value = '1';
    document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { source: 'heritage-limit' } }));
  } catch {
    // swallow — reconcile will try again on next cart event (i.e. cart update)
  } finally {
    correctingLines.delete(line);
  }
}

function enforceHeritageLineLimits() {
  document
    .querySelectorAll('quantity-selector-component[data-heritage-cart-line="true"]')
    .forEach((el) => {
      const input = el.querySelector('input[type="number"]');
      if (!input) return;
      const line = input.getAttribute('data-cart-line');
      const qty = Number(/** @type {HTMLInputElement} */ (input).value);
      if (line && qty > 1) {
        enforceHeritageLineLimit(el, line);
      }
    });
}

async function reconcile() {
  const atcEls = document.querySelectorAll('add-to-cart-component[data-heritage-source="true"][data-product-id]');
  const qtyEls = document.querySelectorAll('quantity-selector-component[data-heritage-source="true"][data-product-id]');
  const usfBtns = document.querySelectorAll('button.product-card__add-to-cart[data-heritage-source="true"][data-product-id]');
  if (!atcEls.length && !qtyEls.length && !usfBtns.length) return;

  const ids = await fetchCartProductIds();
  optimisticallyLocked.clear();

  atcEls.forEach((el) => applyAddToCartState(el, ids.has(String(/** @type {HTMLElement} */ (el).dataset.productId))));
  qtyEls.forEach((el) => applyQuantityState(el, ids.has(String(/** @type {HTMLElement} */ (el).dataset.productId))));
  usfBtns.forEach((btn) => applyUsfButtonState(btn, ids.has(String(/** @type {HTMLElement} */ (btn).dataset.productId))));

  enforceHeritageLineLimits();
}

/**
 * @param {HTMLFormElement | Element | EventTarget | null} form
 * @returns {string | null}
 */
function getHeritageProductIdForForm(form) {
  if (!form || !('querySelector' in form)) return null;
  const atc = form.querySelector('add-to-cart-component[data-heritage-source="true"][data-product-id]');
  if (atc) return String(/** @type {HTMLElement} */ (atc).dataset.productId);
  const usfBtn = form.querySelector('button.product-card__add-to-cart[data-heritage-source="true"][data-product-id]');
  if (usfBtn) return String(/** @type {HTMLElement} */ (usfBtn).dataset.productId);
  return null;
}

document.addEventListener(
  'submit',
  (event) => {
    const form = /** @type {HTMLFormElement} */ (event.target);
    const productId = getHeritageProductIdForForm(form);
    if (!productId) return;

    if (optimisticallyLocked.has(productId)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    optimisticallyLocked.add(productId);
    setTimeout(() => lockForProduct(productId), 0);
  },
  true
);

document.addEventListener('heritage:reconcile', () => reconcile());

document.addEventListener(ThemeEvents.cartUpdate, () => {
  reconcile();
  setTimeout(reconcile, 250);
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', reconcile);
} else {
  reconcile();
}
